"use client";

/* eslint-disable react/prop-types */
import { useEffect, useState } from "react";
import Image from "next/image";
import Link from "next/link";
import { ChevronUp } from "lucide-react";
import { db } from "@kkeut/lib/firebase/client";
import { getDoc, doc } from "firebase/firestore";

const withHttps = (url) => {
  if (!url) return "";
  const s = String(url).trim();
  if (s.startsWith("weixin://")) return s; // 위챗 스킴 예외
  return s.startsWith("http://") || s.startsWith("https://") ? s : `https://${s}`;
};

/**
 * 우측 고정 Quick Menu. Firestore `{branchId}/default` 문서에서 SNS 링크 fetch.
 *
 * 사용 방식:
 *   - `branchId` 만 주면 컴포넌트가 직접 fetch (대부분의 지점)
 *   - 또는 `initialInfo` 직접 주입 (SSR 패턴, guro)
 *
 * @param {object} props
 * @param {string} [props.branchId] - Firestore document key (예: "hongdae")
 * @param {object} [props.initialInfo] - SSR 으로 미리 받은 데이터 (있으면 fetch 안 함)
 * @param {string} [props.aboutHref] - "/about/kkeut" 같은 병원소개 경로 (locale 처리된 값)
 * @param {boolean} [props.hidden] - true 면 미렌더 (예: 멤버 프리뷰 페이지)
 */
export function QuickMenu({
  branchId,
  initialInfo = null,
  aboutHref = "/about/kkeut",
  hidden = false,
}) {
  const [info, setInfo] = useState(initialInfo);

  useEffect(() => {
    if (initialInfo) return;
    if (!branchId) return;
    let cancelled = false;
    (async () => {
      try {
        const snap = await getDoc(doc(db, branchId, "default"));
        if (cancelled) return;
        setInfo(snap.exists() ? snap.data() : null);
      } catch (e) {
        if (!cancelled) console.error("QuickMenu fetch 실패:", e);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [branchId, initialInfo]);

  if (hidden) return null;
  if (!info) return null;

  const safeNaver = withHttps(info?.naverLink);
  const safeKakao = withHttps(info?.kakaoLink);
  const safeInstagram = withHttps(info?.instagramLink);
  const safeWhatsapp = withHttps(info?.whatsappLink);
  const safeWechat = withHttps(info?.wechatLink);
  const safeLine = withHttps(info?.lineLink);

  const scrollToTop = () => {
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const buttonClass = "rounded-full bg-[#f1a1af] p-3 shadow-lg transition hover:scale-110";

  return (
    <div className="fixed bottom-16 right-10 z-50 hidden lg:flex flex-col items-center gap-3">
      {safeNaver && (
        <a
          href={safeNaver}
          target="_blank"
          rel="noopener noreferrer"
          className={buttonClass}
          aria-label="Naver Booking"
        >
          <Image src="/assets/naverCalendarLogo.png" width={24} height={24} alt="네이버예약" />
        </a>
      )}

      {safeKakao && (
        <a
          href={safeKakao}
          target="_blank"
          rel="noopener noreferrer"
          className={buttonClass}
          aria-label="Kakao Chat"
        >
          <Image src="/assets/kakaoLogo.png" width={24} height={24} alt="카카오톡상담" />
        </a>
      )}

      {safeInstagram && (
        <a
          href={safeInstagram}
          target="_blank"
          rel="noopener noreferrer"
          className={buttonClass}
          aria-label="Instagram"
        >
          <Image src="/assets/instalogo.png" width={24} height={24} alt="인스타그램" />
        </a>
      )}

      {safeWhatsapp && (
        <a
          href={safeWhatsapp}
          target="_blank"
          rel="noopener noreferrer"
          className={buttonClass}
          aria-label="WhatsApp"
        >
          <Image src="/assets/whatsapplogo.png" width={24} height={24} alt="WhatsApp" />
        </a>
      )}

      {safeWechat && (
        <a
          href={safeWechat}
          target="_blank"
          rel="noopener noreferrer"
          className={buttonClass}
          aria-label="WeChat"
        >
          <Image src="/assets/wechatlogo.png" width={24} height={24} alt="WeChat" />
        </a>
      )}

      {safeLine && (
        <a
          href={safeLine}
          target="_blank"
          rel="noopener noreferrer"
          className={buttonClass}
          aria-label="LINE"
        >
          <Image src="/assets/linelogo.png" width={24} height={24} alt="LINE" />
        </a>
      )}

      <Link href={aboutHref} className={buttonClass} aria-label="About Clinic">
        <Image src="/assets/hospital.png" width={24} height={24} alt="병원소개" />
      </Link>

      <button
        type="button"
        onClick={scrollToTop}
        className={buttonClass}
        aria-label="맨 위로"
      >
        <ChevronUp size={20} color="#fff" />
      </button>
    </div>
  );
}
